// Animation helpers
export { initFadeInUpAnimation } from './fadeInUpAnimations';

// Validation helpers
export {
  validateInput,
  validateSearchInput,
  isSearchInputValid,
  getValidationError,
  type ValidationOptions,
  type ValidationResult
} from './inputValidation';

export const getEnvGQLUrl = () => {
  const baseUrl = import.meta.env.VITE_OPTIGRAPHQL_BASE_URL as string;

  if (document.querySelector('body')?.getAttribute('graph-env') === 'prd') {
    return `${baseUrl}${import.meta.env.VITE_OPTIGRAPHQL_PROD_KEY}`;
  } else if (document.querySelector('body')?.getAttribute('graph-env') === 'stg') {
    return `${baseUrl}${import.meta.env.VITE_OPTIGRAPHQL_STG_KEY}`;
  } else {
    return `${baseUrl}${import.meta.env.VITE_OPTIGRAPHQL_INTE_KEY}`;
  }
};

export const setValuesByStringToken = (string: string, values: string[]) => {
  return string.replace(/\{([0-9]+)\}/g, (_, p1) => values[parseInt(p1) - 1]);
};

export const getSearchTerm = (searchTerm: string) => (searchTerm ? `%${searchTerm}%` : undefined);

export const getSearchArray = (array: string[]) => (array?.length ? array : undefined);

export const getBrandFromListing = () => {
  const brand = (document.querySelector('[data-module="listing"]') as HTMLElement)?.dataset?.brand;
  return brand;
};

// Date formatting utilities
class DateFormatter {
  private static readonly FORMAT_TOKENS = {
    YYYY: (date: Date) => date.getFullYear().toString(),
    YY: (date: Date) => date.getFullYear().toString().slice(-2),
    MMM: (date: Date) =>
      ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
        date.getMonth()
      ],
    MM: (date: Date) => (date.getMonth() + 1).toString().padStart(2, '0'),
    M: (date: Date) => (date.getMonth() + 1).toString(),
    DD: (date: Date) => date.getDate().toString().padStart(2, '0'),
    D: (date: Date) => date.getDate().toString(),
    ddd: (date: Date) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()],
    dd: (date: Date) => ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][date.getDay()],
    d: (date: Date) => date.getDay().toString(),
    HH: (date: Date) => date.getHours().toString().padStart(2, '0'),
    H: (date: Date) => date.getHours().toString(),
    mm: (date: Date) => date.getMinutes().toString().padStart(2, '0'),
    m: (date: Date) => date.getMinutes().toString(),
    ss: (date: Date) => date.getSeconds().toString().padStart(2, '0'),
    s: (date: Date) => date.getSeconds().toString(),
    SSS: (date: Date) => date.getMilliseconds().toString().padStart(3, '0'),
    S: (date: Date) => date.getMilliseconds().toString(),
    Z: (date: Date) => {
      const offset = date.getTimezoneOffset();
      const sign = offset <= 0 ? '+' : '-';
      const hours = Math.abs(Math.floor(offset / 60))
        .toString()
        .padStart(2, '0');
      const minutes = Math.abs(offset % 60)
        .toString()
        .padStart(2, '0');
      return `${sign}${hours}:${minutes}`;
    }
  } as const;

  private static isCustomFormat(format: string): boolean {
    return Object.keys(this.FORMAT_TOKENS).some(token => format.includes(token));
  }

  private static isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }

  private static formatWithCustomPattern(date: Date, format: string): string {
    // Handle invalid dates
    if (!this.isValidDate(date)) {
      return 'Invalid Date';
    }

    const tokenPattern = new RegExp(`(${Object.keys(this.FORMAT_TOKENS).join('|')})`, 'g');

    return format.replace(tokenPattern, match => {
      const formatter = this.FORMAT_TOKENS[match as keyof typeof this.FORMAT_TOKENS];
      return formatter ? formatter(date) : match;
    });
  }

  static format(date: Date, format: string): string {
    // Handle invalid dates first
    if (!this.isValidDate(date)) {
      return 'Invalid Date';
    }

    if (this.isCustomFormat(format)) {
      return this.formatWithCustomPattern(date, format);
    }

    // Fallback to locale-based formatting
    try {
      return date.toLocaleDateString(format);
    } catch (error) {
      // If locale formatting fails, fall back to ISO string
      console.warn(`Failed to format date with locale '${format}':`, error);
      return date.toISOString().split('T')[0]; // Return YYYY-MM-DD format
    }
  }

  // Additional utility methods for common scenarios
  static formatISO(date: Date): string {
    return this.isValidDate(date) ? date.toISOString() : 'Invalid Date';
  }

  static formatUTC(date: Date): string {
    return this.isValidDate(date) ? date.toUTCString() : 'Invalid Date';
  }

  static formatLocale(date: Date, locale?: string, options?: Intl.DateTimeFormatOptions): string {
    if (!this.isValidDate(date)) {
      return 'Invalid Date';
    }

    try {
      return date.toLocaleDateString(locale, options);
    } catch (error) {
      console.warn(`Failed to format date with locale '${locale}':`, error);
      return date.toLocaleDateString(); // Fallback to default locale
    }
  }

  static getRelativeTime(date: Date, baseDate: Date = new Date()): string {
    if (!this.isValidDate(date) || !this.isValidDate(baseDate)) {
      return 'Invalid Date';
    }

    const diffInMs = baseDate.getTime() - date.getTime();
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }
}

export const getPublicationDate = (publicationDate: string): string => {
  const dateFormat = document.body.getAttribute('data-listing-date-format') || 'en-US';
  const date = new Date(publicationDate);

  return DateFormatter.format(date, dateFormat);
};

/**
 * Checks if the user has enabled reduced motion preference
 * @returns {boolean} True if reduced motion is preferred, false otherwise
 */
export const prefersReducedMotion = (): boolean => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};
